import React, { useState, useEffect, useCallback, useRef } from "react";
import { widgetApi } from "../services/api";
import { useToast } from "./Toast";
import { CheckIcon, CopyIcon } from "./icons";
import errorTracker from "../utils/errorTracker";
import "./EmbedCodeGenerator.css";

// Constants
const COPY_FEEDBACK_DURATION_MS = 2000;

const EmbedCodeGenerator = ({ widgetId, widgetKey }) => {
  const [embedCode, setEmbedCode] = useState("");
  const [displayWidgetKey, setDisplayWidgetKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const toast = useToast();
  const copiedTimeoutRef = useRef(null);

  const fetchEmbedCode = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await widgetApi.getEmbedCode(widgetId);
      setEmbedCode(response?.embedCode || "");
      setDisplayWidgetKey(response?.widgetKey || widgetKey || "");
    } catch (err) {
      errorTracker.captureException(err, {
        context: "fetchEmbedCode",
        widgetId,
      });

      // Generic error message (don't leak backend details)
      setError("Failed to load embed code. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [widgetId, widgetKey]);

  useEffect(() => {
    fetchEmbedCode();
  }, [fetchEmbedCode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const copyToClipboardFallback = (text) => {
    // Fallback method for older browsers or non-HTTPS contexts
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand("copy");
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopy = async () => {
    // Clear any existing timeout
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }

    try {
      setCopying(true);

      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(embedCode);
        setCopied(true);
        toast.success("Embed code copied to clipboard!");

        copiedTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          copiedTimeoutRef.current = null;
        }, COPY_FEEDBACK_DURATION_MS);
      } else {
        // Fallback for older browsers or HTTP
        const success = copyToClipboardFallback(embedCode);
        if (success) {
          setCopied(true);
          toast.success("Embed code copied to clipboard!");

          copiedTimeoutRef.current = setTimeout(() => {
            setCopied(false);
            copiedTimeoutRef.current = null;
          }, COPY_FEEDBACK_DURATION_MS);
        } else {
          throw new Error("Copy failed");
        }
      }
    } catch (err) {
      errorTracker.captureException(err, { context: "copyEmbedCode" });
      toast.error("Failed to copy. Please copy the code manually.");
    } finally {
      setCopying(false);
    }
  };

  if (loading) {
    return (
      <div className="embed-code-generator">
        <div className="embed-code-loading">Loading embed code...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="embed-code-generator">
        <div className="embed-code-error">
          {error}
          <button onClick={fetchEmbedCode} className="embed-code-retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="embed-code-generator">
      <div className="embed-code-header">
        <h3 className="embed-code-title">Embed Code</h3>
        <button
          onClick={handleCopy}
          className={`embed-code-copy-btn ${copied ? "embed-code-copied" : ""}`}
          disabled={!embedCode || copying}
        >
          {copying ? (
            <>
              <span className="embed-code-spinner"></span>
              Copying...
            </>
          ) : copied ? (
            <>
              <CheckIcon size={16} />
              Copied!
            </>
          ) : (
            <>
              <CopyIcon size={16} />
              Copy Code
            </>
          )}
        </button>
      </div>

      <div className="embed-code-container">
        <pre className="embed-code-pre">
          <code>{embedCode}</code>
        </pre>
      </div>

      <div className="embed-code-instructions">
        <h4 className="embed-code-instructions-title">
          Installation Instructions
        </h4>
        <ol className="embed-code-instructions-list">
          <li>Copy the embed code above</li>
          <li>
            Paste it into your website's HTML, just before the closing{" "}
            <code>&lt;/body&gt;</code> tag
          </li>
          <li>The widget will automatically appear on your website</li>
          <li>Customize the appearance in your widget settings</li>
        </ol>

        <div className="embed-code-note">
          <div className="embed-code-note-content">
            <p className="embed-code-note-title">
              <strong>Widget Key (Public Identifier):</strong>
            </p>
            <code className="embed-code-widget-key">
              {displayWidgetKey || "Loading..."}
            </code>
            <p className="embed-code-note-text">
              This public key identifies your widget and is safe to include in
              your website's code. Your widget is protected by domain
              whitelisting and rate limiting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbedCodeGenerator;
